import { Store } from '@betgames/bg-state-manager';
import { StorageRepository } from '@betgames/bg-tools';
import {
    IAuthenticateResponse,
    IAuthParsedParams,
    IAuthState,
    IReverseAuthenticateResponse,
} from 'store/Auth/interfaces';
import SentryRepository from 'repositories/SentryRepository/SentryRepository';
import WebSocketsRepository from 'repositories/WebSockets/WebSockets.repository';
import { PersistKey } from 'providers/Persist';
import { UNAUTHORIZED_MESSAGE } from 'constants/errors';
import AuthRepository from './Auth.repository';

const EMPTY_TOKEN = '-';

export default class AuthStore extends Store<IAuthState> {
    constructor(
        private readonly repository: AuthRepository,
        private readonly persistor: StorageRepository,
        private readonly sentry: SentryRepository,
        private readonly websocketRepository: WebSocketsRepository,
    ) {
        super({
            key: 'Auth',
            default: {
                authenticated: false,
                token: null,
            },
        });

        const persistedToken = this.persistor.getItem(PersistKey.token);

        if (persistedToken) {
            this.update((draft) => {
                draft.authenticated = true;
                draft.token = persistedToken;
            });
            this.repository.setToken(persistedToken);
            this.websocketRepository.setToken(persistedToken);
        }
    }

    private setToken = (token: string, forceUpdateToken?: boolean): void => {
        if (token) {
            this.update((draft) => {
                draft.authenticated = true;
                draft.token = token;
            });

            this.repository.setToken(token);
            this.websocketRepository.setToken(token, forceUpdateToken);
            this.persistor.setItem(PersistKey.token, token);
            return;
        }

        this.update((draft) => {
            draft.authenticated = false;
            draft.token = null;
        });
        this.repository.setToken(null);
        this.persistor.removeItem(PersistKey.token);
    };

    private handleSuccess = (
        response: Partial<IAuthenticateResponse | IReverseAuthenticateResponse>,
        partnerCode: string,
    ) => {
        this.setToken(response.token);
        this.sentry.setUser(partnerCode);
    };

    public authenticate = async (params: IAuthParsedParams): Promise<IAuthenticateResponse> => {
        this.repository.setPartnerCode(params.partnerCode);

        if (!params.token || params.token === EMPTY_TOKEN) {
            this.update((draft) => {
                draft.authenticated = false;
            });
            this.repository.setToken(null);

            return Promise.resolve({
                currency: null,
                settings: null,
                token: null,
                limits: null,
            });
        }

        try {
            const response = await this.repository.authenticate({
                token: params.token,
            });

            if (response.token) {
                this.handleSuccess(response, params.partnerCode);
                return response;
            }
            this.setToken(null);
        } catch (error) {
            this.setToken(null);
        }

        return Promise.resolve({
            currency: null,
            settings: null,
            token: null,
            limits: null,
        });
    };

    public authenticateReverse = async (
        query: string,
    ): Promise<Partial<IReverseAuthenticateResponse>> =>
        this.repository
            .authenticateReverse(query)
            .catch((error) => {
                if (!error?.integration?.partnerCode) {
                    throw new Error(
                        'License is not found (partner code is not available), please contact support',
                    );
                }

                return {
                    token: null,
                    partnerCode: error?.integration?.partnerCode,
                    integration: error?.integration,
                    currency: error?.integration?.currency,
                    settings: error?.integration?.settings,
                } as Partial<IReverseAuthenticateResponse>;
            })
            .then((response) => {
                if (response?.integration?.partnerCode) {
                    this.repository.setPartnerCode(response.integration.partnerCode);
                }

                if (response.token) {
                    this.handleSuccess(response, response?.integration?.partnerCode);
                }

                return response;
            });

    public refreshTokenWorker(frequency: number, initImmediately?: boolean): void {
        let cancelInterval: number;
        const worker = (forceUpdateToken?: boolean) => {
            this.repository
                .refreshToken()
                .then((response) => {
                    if (response.token) {
                        this.setToken(response.token, forceUpdateToken);
                        return;
                    }

                    if (cancelInterval) {
                        window.clearInterval(cancelInterval);
                    }
                    this.setToken(null);
                })
                .catch((error) => {
                    if (error.message === UNAUTHORIZED_MESSAGE) {
                        if (cancelInterval) {
                            window.clearInterval(cancelInterval);
                        }

                        this.setToken(null);
                    }
                    console.error('Refresh token error: ', error);
                });
        };

        if (initImmediately) {
            worker(true);
        }
        cancelInterval = window.setInterval(worker, frequency);
    }
}
